import { FILE_ICONS } from '@/constants';
import { useDialogFocus } from '@/hooks/use-dialog-focus';
import { useIDESelectors } from '@/hooks/use-ide-selector';
import { useIDEState } from '@/hooks/use-ide-state';
import { trackEvent } from '@/utils/analytics';
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

interface SearchResult {
    id: string;
    fileId: string;
    projectId: string;
    fileName: string;
    projectName: string;
    path: string;
    type: 'component' | 'iframe' | 'game';
    score: number;
}

interface FileSearchProps {
    isOpen: boolean;
    onClose: () => void;
}

export const FileSearch: React.FC<FileSearchProps> = ({ isOpen, onClose }) => {
    const { state, openFile, addOutputMessage } = useIDEState();
    const dialogRef = useDialogFocus(isOpen, onClose);
    const projects = useIDESelectors.useProjects();

    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listId = useId();

    const inputRef = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);

    // Fuzzy search algorithm
    const fuzzySearch = useCallback((text: string, pattern: string): number => {
        if (!pattern) return 0;

        const textLower = text.toLowerCase();
        const patternLower = pattern.toLowerCase();

        // Exact match gets highest score
        if (textLower === patternLower) return 100;

        // Starts with pattern gets high score
        if (textLower.startsWith(patternLower)) return 90;

        // Contains pattern gets medium score
        if (textLower.includes(patternLower)) return 70;

        // Fuzzy matching - check if all pattern characters exist in order
        let patternIndex = 0;
        let score = 0;

        for (let i = 0; i < textLower.length && patternIndex < patternLower.length; i++) {
            if (textLower[i] === patternLower[patternIndex]) {
                score += 10;
                patternIndex++;
            }
        }

        // If all pattern characters were found, return the score
        if (patternIndex === patternLower.length) {
            return Math.max(score, 30);
        }

        return 0;
    }, []);

    // Search through all files
    const searchFiles = useCallback(
        (searchQuery: string): SearchResult[] => {
            const searchResults: SearchResult[] = [];

            projects.forEach(project => {
                project.files.forEach(file => {
                    // Search in file name
                    const fileNameScore = fuzzySearch(file.name, searchQuery);
                    // Search in project name
                    const projectNameScore = fuzzySearch(project.name, searchQuery);
                    // Search in file path
                    const pathScore = fuzzySearch(file.path, searchQuery);

                    // Take the highest score
                    const maxScore = Math.max(fileNameScore, projectNameScore, pathScore);

                    if (!searchQuery.trim() || maxScore > 0) {
                        searchResults.push({
                            id: `${project.id}-${file.id}`,
                            fileId: file.id,
                            projectId: project.id,
                            fileName: file.name,
                            projectName: project.name,
                            path: file.path,
                            type: file.type,
                            score: maxScore,
                        });
                    }
                });
            });

            // Sort by score (highest first), then by name
            return searchResults.sort((a, b) => {
                if (a.score !== b.score) return b.score - a.score;
                return a.fileName.localeCompare(b.fileName);
            });
        },
        [projects, fuzzySearch]
    );

    const results = useMemo(() => {
        const matches = searchFiles(query.trim());
        if (query.trim()) return matches;
        const recent = state.recentFiles
            .map(id => matches.find(file => file.fileId === id))
            .filter((file): file is SearchResult => Boolean(file));
        return (recent.length ? recent : matches).slice(0, 8);
    }, [query, searchFiles, state.recentFiles]);
    const activeIndex = Math.min(selectedIndex, Math.max(0, results.length - 1));
    const openResult = useCallback(
        (result: SearchResult) => {
            openFile(result.fileId, result.projectId);
            addOutputMessage(`Opened file via search: ${result.projectName}/${result.fileName}`, 'info');
            trackEvent('search_result_clicked', { file_id: result.fileId, file_name: result.fileName });
            onClose();
        },
        [openFile, addOutputMessage, onClose]
    );
    useEffect(() => {
        if (isOpen && query.trim()) trackEvent('search_performed', { query, result_count: results.length });
    }, [query, results.length, isOpen]);
    useEffect(() => {
        resultsRef.current?.children[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
    }, [activeIndex, isOpen]);

    // Focus input when search opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isOpen]);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setSelectedIndex(
                Math.max(0, Math.min(results.length - 1, activeIndex + (event.key === 'ArrowDown' ? 1 : -1)))
            );
        } else if (event.key === 'Enter' && results[activeIndex]) {
            event.preventDefault();
            openResult(results[activeIndex]);
        }
    };

    // Get file icon
    const getFileIcon = useCallback((fileName: string, type: string) => {
        if (type === 'game') return '🎮';
        if (type === 'iframe') return '🌐';
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'html':
                return FILE_ICONS.HTML;
            default:
                return FILE_ICONS.DEFAULT;
        }
    }, []);

    // Highlight matching text
    const highlightText = useCallback((text: string, query: string) => {
        if (!query.trim()) return text;

        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <mark key={index} className='bg-yellow-400/30 text-yellow-200'>
                    {part}
                </mark>
            ) : (
                part
            )
        );
    }, []);

    if (!isOpen) return null;

    return (
        <div className='fixed inset-0 z-50 flex items-start justify-center pt-20'>
            {/* Backdrop */}
            <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={onClose} />

            {/* Search Modal */}
            <div
                ref={dialogRef}
                role='dialog'
                aria-modal='true'
                aria-label='Search files and projects'
                tabIndex={-1}
                className='relative w-full max-w-2xl mx-4 bg-vscode-bg-secondary border border-gray-600/30 rounded-lg shadow-2xl'
            >
                {/* Search Input */}
                <div className='p-4 border-b border-gray-600/30'>
                    <div className='flex items-center space-x-3'>
                        <div className='text-vscode-text-muted'>🔍</div>
                        <input
                            ref={inputRef}
                            type='text'
                            value={query}
                            onChange={e => {
                                setQuery(e.target.value);
                                setSelectedIndex(0);
                            }}
                            role='combobox'
                            aria-label='Search files and projects'
                            aria-expanded={true}
                            aria-autocomplete='list'
                            aria-controls={listId}
                            aria-activedescendant={results.length ? `${listId}-${results[activeIndex].id}` : undefined}
                            onKeyDown={handleKeyDown}
                            placeholder='Search files and projects...'
                            className='flex-1 bg-transparent text-vscode-text placeholder-vscode-text-muted outline-none text-lg'
                        />
                        <div aria-live='polite' className='text-vscode-text-muted text-sm'>
                            {results.length} {results.length === 1 ? 'result' : 'results'}
                        </div>
                        <button
                            type='button'
                            onClick={onClose}
                            aria-label='Close search'
                            className='min-w-8 min-h-8 rounded text-vscode-text-secondary'
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Results */}
                <div
                    ref={resultsRef}
                    id={listId}
                    role='listbox'
                    aria-label={query.trim() ? 'Search results' : 'Suggested files'}
                    className='max-h-[40vh] overflow-y-auto'
                >
                    {results.length === 0 ? (
                        <div className='p-8 text-center text-vscode-text-muted'>
                            {query.trim()
                                ? 'No files found. Try a project name or a shorter search.'
                                : 'No files available'}
                        </div>
                    ) : (
                        results.map((result, index) => (
                            <div
                                key={result.id}
                                id={`${listId}-${result.id}`}
                                role='option'
                                aria-selected={index === activeIndex}
                                className={`flex items-center p-3 cursor-pointer border-b border-gray-600/20 last:border-b-0 transition-colors text-vscode-text-secondary hover:bg-gray-600/40 hover:text-vscode-text ${index === activeIndex ? 'bg-vscode-bg-tertiary ring-1 ring-inset ring-vscode-accent' : ''}`}
                                onMouseMove={() => setSelectedIndex(index)}
                                onClick={() => openResult(result)}
                            >
                                <div className='mr-3 text-lg'>{getFileIcon(result.fileName, result.type)}</div>
                                <div className='flex-1 min-w-0'>
                                    <div className='font-medium truncate'>{highlightText(result.fileName, query)}</div>
                                    <div className='text-sm text-vscode-text-muted truncate'>
                                        {highlightText(result.projectName, query)} • {result.path}
                                    </div>
                                </div>
                                <div className='ml-3 text-xs text-vscode-text-muted'>{result.type}</div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                <div className='p-3 border-t border-gray-600/30 bg-vscode-bg-tertiary rounded-b-lg'>
                    <div className='flex items-center justify-between text-xs text-vscode-text-muted'>
                        <div className='flex items-center space-x-4'>
                            <span>↑ ↓ Navigate · Enter Open</span>
                            <span>Esc Close</span>
                        </div>
                        <div>Ctrl+Shift+F</div>
                    </div>
                </div>
            </div>
        </div>
    );
};
